import React, { useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, makeStyles } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { setCurrentLivestockSaleId } from "actions";

import { ClickableCommentIcon } from "components/Comments/Icon";
import { PreventPropagation } from "components/Events";
import { Column, Row } from "components/Layout";
import { Text } from "components/Text";

import { CommentTypes } from "constants/comments";
import { getIconForSpeciesId } from "constants/species";

import { formatHeaderDateString } from "lib/timeFormats";

import {
  getAgentsDeploymentSaleBySaleId,
  getEffectiveSaleTitleBySaleId,
  getIsLivestockAgent,
  getIsOnline,
  getSaleById,
  selectCanCreateExternalAgentSaleFromSale,
} from "selectors";
import { useCanCreateSellViaExAgentSaleFromSale, useIsMobile } from "hooks";
import { faCopy } from "@fortawesome/pro-solid-svg-icons";
import Button from "components/Button";
import {
  openCreateExAgentSaleModal,
  openCreateSellViaExAgentSaleModal,
} from "lib/navigation";
import { ContextMenuPopoverButton } from "components/Button/MultiButton";
import { SaleStatusComponent } from "./StatusSelect";

const IconContainer = styled.div`
  font-size: 20px;
  width: 42px;
  height: 42px;
  background: ${({ theme }) => theme.colors.grayDisabled};
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 6px;
`;

const SaleRowColumns = styled.div`
  font-size: 14px;
  font-weight: normal;
  margin: auto 12px;
  @media (max-width: ${({ theme }) => theme.breakpoints[1]}px) {
    margin: auto 6px;
  }
  color: ${({ theme }) => theme.colors.gray40};
  flex-basis: 0;
  flex-grow: 1;
  justify-content: space-between;
`;

const SaleRow = styled(Row)`
  box-shadow: ${({ theme }) => theme.shadows[1]};
  padding: 10px;
  margin: ${({ isMobile }) => (!isMobile ? "4px 0px 4px" : "2px -20px 2px")};
  position: relative;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.colors.primaryHighlight}20;
  }
`;

const BlueBar = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  width: 10px;
  margin-right: 5px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
`;

const MultiButtonComponent = styled(Button)(
  ({ theme }) => `
  padding: ${theme.space[0]}px ${theme.space[2]}px;
  border-radius: 5px;
`,
);

const useStyles = makeStyles(theme => ({
  gridItem: {
    [theme.breakpoints.down("xs")]: {
      maxWidth: "none !important",
      flexBasis: "auto !important",
    },
  },

  gridStatus: {
    [theme.breakpoints.down("xs")]: {
      width: "35%",
    },
  },
  gridSaleDate: {
    [theme.breakpoints.down("xs")]: {
      width: "17%",
    },
  },
  gridSaleType: {
    [theme.breakpoints.down("xs")]: {
      width: "19%",
    },
  },
  gridSaleCode: {
    [theme.breakpoints.down("xs")]: {
      width: "15%",
    },
  },
  gridSaleComment: {
    [theme.breakpoints.down("xs")]: {
      width: "12%",
    },
  },
  gridSaleActionButton: {
    [theme.breakpoints.down("xs")]: {
      width: "5%",
    },
  },
}));

interface DashboardSaleCardProps {
  saleId: number;
}

const DashboardSaleCard = React.memo(
  ({ saleId }: DashboardSaleCardProps): React.JSX.Element => {
    const dispatch = useDispatch();
    const livestockSale = useSelector(getSaleById(saleId));
    const saleDate = new Date(livestockSale.date);
    const namedDate = saleDate ? formatHeaderDateString(saleDate) : "";

    const classes = useStyles();
    const isMobile = useIsMobile();

    const isLivestockAgent = useSelector(getIsLivestockAgent);

    const canCreateExternalAgentSaleFromSale = useSelector(
      selectCanCreateExternalAgentSaleFromSale,
    );

    const deploymentSale = useSelector(getAgentsDeploymentSaleBySaleId(saleId));

    const saleTitle = useSelector(getEffectiveSaleTitleBySaleId(saleId));

    const isOnline = useSelector(getIsOnline);

    const goToSale = () =>
      dispatch(setCurrentLivestockSaleId(saleId, true, false));

    const handleParentClick = event => {
      if (!event.defaultPrevented) {
        goToSale();
      }
    };

    const canCreateSellViaExternalAgentSaleFromSale =
      useCanCreateSellViaExAgentSaleFromSale(saleId);

    const additionalActions = useMemo(
      () =>
        [
          canCreateExternalAgentSaleFromSale
            ? {
                title: "Create Ex Agent Sale",
                icon: faCopy,
                isDisabled: !isOnline,
                onClick: () => openCreateExAgentSaleModal(saleId),
              }
            : null,
          canCreateSellViaExternalAgentSaleFromSale
            ? {
                title: "Create Sell via Ex Agent Sale",
                icon: faCopy,
                isDisabled: !isOnline,
                onClick: () => openCreateSellViaExAgentSaleModal(saleId),
              }
            : null,
        ].filter(Boolean),
      [
        canCreateExternalAgentSaleFromSale,
        canCreateSellViaExternalAgentSaleFromSale,
        isOnline,
        saleId,
      ],
    );

    return (
      <Column data-tour="saleCard">
        <SaleRow
          data-tour={livestockSale.sale_code}
          sale={livestockSale}
          isMobile={isMobile}
          onClick={handleParentClick}
        >
          {!isMobile && <BlueBar />}
          <IconContainer isMobile={isMobile}>
            <FontAwesomeIcon
              icon={getIconForSpeciesId(livestockSale?.species_id)}
            />
          </IconContainer>
          <Grid alignItems="center" container>
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <Text marginHorizontal={1} fontSize="charlie">
                {saleTitle}
              </Text>
            </Grid>
            <Grid
              item
              xs={2}
              className={`${classes.gridSaleDate} ${classes.gridItem}`}
            >
              <SaleRowColumns>{namedDate}</SaleRowColumns>
            </Grid>
            <Grid
              item
              xs={2}
              className={`${classes.gridSaleType} ${classes.gridItem}`}
            >
              <SaleRowColumns>{livestockSale.sale_type}</SaleRowColumns>
            </Grid>
            <Grid
              item
              xs={1}
              className={`${classes.gridSaleCode} ${classes.gridItem}`}
            >
              <SaleRowColumns>{livestockSale.sale_code}</SaleRowColumns>
            </Grid>
            {isLivestockAgent && (
              <Grid
                item
                xs={1}
                className={`${classes.gridSaleComment} ${classes.gridItem}`}
              >
                <SaleRowColumns>
                  <PreventPropagation>
                    <ClickableCommentIcon
                      commentType={CommentTypes.DEPLOYMENT_SALE}
                      commentTypeId={deploymentSale.deployment_sale_id}
                      returnTo={window.location.hash}
                      options={{}}
                    />
                  </PreventPropagation>
                </SaleRowColumns>
              </Grid>
            )}
            <Grid
              item
              xs={2}
              className={`${classes.gridStatus} ${classes.gridItem}`}
            >
              <SaleRowColumns>
                <SaleStatusComponent saleId={saleId} />
              </SaleRowColumns>
            </Grid>
            {additionalActions.length > 0 && (
              <Grid
                item
                xs={1}
                className={`${classes.gridSaleActionButton} ${classes.gridItem}`}
              >
                <PreventPropagation>
                  <ContextMenuPopoverButton
                    buttons={additionalActions}
                    label=""
                    ButtonComponent={MultiButtonComponent}
                  />
                </PreventPropagation>
              </Grid>
            )}
          </Grid>
        </SaleRow>
      </Column>
    );
  },
);

export default DashboardSaleCard;
